import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import Joi from '@hapi/joi';
import { Formik } from 'formik';
import cn from 'classnames';
import { toast } from 'react-toastify';
import moment from 'moment';
import { CSSTransition } from 'react-transition-group';
import { useQuery, useMutation, gql } from '@apollo/client';
import Lottie from 'react-lottie-player';
import { useHistory, useLocation } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import 'mutationobserver-shim';
import ReactGA from 'Shared/analytics';
import jsCookie from 'js-cookie';

import Icon from 'Components/Icon';
import Tabs from 'Components/TabNav';
import Button from 'Components/Button';
import TabPersonal from './components/TabPersonal';
import TabPersonalCustomer from './components/TabPersonal/TabPersonalCustomer';
import TabInventory from './components/TabInventory';
import TabCompany from './components/TabCompany';
import querystring from 'RootShared/utility/querystring';
import withAuthentication from 'Components/withAuthentication';
import animationData from 'Shared/spiner_blue.json';
import { CAN_EDIT_COMPANY } from './constants/constants';
import useLogOut from 'Shared/hooks/useLogOut';
import strToBool from 'Shared/util/strToBool';

export const mutationLogOut = gql`
  mutation ProfileLogOutMutation @api(name: root) {
    logout {
      result
    }
  }
`;

const mutationProfileChangePersonalData = gql`
  mutation ProfileChangeUserDataMutation($input: EmployeeProfileInput!) @api(name: root) {
    updateEmployee(employee: $input) {
      employee {
        user {
          email
          firstName
          lastName
          employeeprofile {
            birthDate
            github
            linkedin
            phone
            telegram
            avatar
          }
          id
        }
      }
    }
  }
`;

export const mutationProfileCustomerPersonalData = gql`
  mutation ProfileCustomerUserDataMutation($input: CustomerProfileUpdateMutationInput!) @api(name: root) {
    updateCustomer(input: $input) {
      customer {
        user {
          email
          firstName
        }
      }
    }
  }
`;

const mutationInventoryData = gql`
  mutation ProfileInventoryDataMutation($records: [StocktakingInputRecordType]!) @api(name: root) {
    bulkStocktakingRecord(records: $records) {
      records {
        id
      }
    }
  }
`;

export const queryGetProfileData = gql`
  query AppGetProfileDataQuery @api(name: root) {
    user {
      email
      firstName
      lastName
      permissions
      config {
        logo
        extraLinks {
          id
          url
          anchor
          supportText
          icon
        }
      }
      isStaff
      employeeprofile {
        birthDate
        github
        linkedin
        phone
        telegram
        avatar
        mobileProjects {
          id
          title
        }
      }
      customerprofile {
        contact {
          firstName
          lastName
          company {
            country {
              name
              id
            }
            city {
              name
              id
            }
            title
            address1
            zip
            email
          }
        }
      }
      id
    }
  }
`;

const Profile = () => {
  const history = useHistory();
  const { search, pathname } = useLocation();
  const client = useApolloClient();
  const [logOut] = useLogOut();

  const {
    loading: loadingProfileData,
    error: profileDataGettingError,
    data: profileData,
    refetch: refetchProfileData,
  } = useQuery(queryGetProfileData, { skip: !strToBool(jsCookie.get('isAuthenticated')) });

  const parsedQuery = useMemo(() => {
    return querystring.parse(search);
  }, [search]);

  const isPersonalTab = parsedQuery.profileTab === 'personal';
  const isIinventoryTab = parsedQuery.profileTab === 'inventory';
  const isCompanyTab = parsedQuery.profileTab === 'company';

  let toastId = useRef(null);

  const showNotification = useCallback(
    () =>
      (toastId.current = toast('In progress, please wait...', {
        autoClose: true,
        closeButton: false,
        hideProgressBar: true,
      })),
    []
  );

  const onProfileMutationSuccess = useCallback(() => {
    refetchProfileData();
    setIsFormDisabled(false);
    toast.update(toastId.current, {
      render: `Profile changes successfully saved`,
      type: toast.TYPE.SUCCESS,
      autoClose: 3000,
      closeButton: true,
      hideProgressBar: false,
    });
  }, [toastId, refetchProfileData]);

  useEffect(() => {
    const profilePage = [...document.getElementsByClassName('app')][0];

    const config = { attributes: true, childList: true };
    const callback = function (mutationsList) {
      for (let mutation of mutationsList) {
        if (mutation.type === 'childList') {
          const addedNodes = [...mutationsList[0].addedNodes];
          document.body.style.overflowY = addedNodes.find(
            (elem) => elem.className === 'profile-page enter enter-active'
          )
            ? 'hidden'
            : 'auto';
        }
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          document.body.style.overflowY = !mutation.oldValue ? 'hidden' : 'auto';
        }
      }
    };
    const observer = new MutationObserver(callback);
    profilePage && observer.observe(profilePage, config);

    return () => {
      observer.disconnect();
    };
  }, []);

  const onProfileMutationError = useCallback(
    (error) => {
      ReactGA.exception({
        description: error.message,
        fatal: true,
      });
      setIsFormDisabled(false);
      toast.update(toastId.current, {
        render: error.message,
        type: toast.TYPE.ERROR,
        autoClose: 3000,
        closeButton: true,
        hideProgressBar: false,
      });
    },
    [toastId]
  );

  const [profileChangePersonalData] = useMutation(mutationProfileChangePersonalData, {
    onCompleted: onProfileMutationSuccess,
    onError: onProfileMutationError,
    update(cache, { data: { updateEmployee } }) {
      if (updateEmployee) {
        cache.modify({
          fields: {
            colleagues(colleagues = [], { readField }) {
              const colleaguesUpdate = colleagues.map((colleague) => {
                if (readField('id', colleague.user) === updateEmployee.employee.user.id) {
                  return {
                    ...colleague,
                    birthDate: updateEmployee.employee.user.employeeprofile.birthDate,
                    phone: updateEmployee.employee.user.employeeprofile.phone,
                    telegram: updateEmployee.employee.user.employeeprofile.telegram,
                    avatar: updateEmployee.employee.user.employeeprofile.avatar,
                  };
                } else return colleague;
              });
              return colleaguesUpdate;
            },
          },
        });
      }
    },
  });

  const tabs = useMemo(
    () => [
      {
        id: 'personal',
        label: 'PERSONAL',
      },
      {
        id: 'inventory',
        label: 'INVENTORY',
      },
    ],
    []
  );

  const tabsCustomer = (customerprofile) => {
    const arr = [
      {
        id: 'personal',
        label: 'PERSONAL',
      },
      {
        id: 'company',
        label: 'COMPANY',
      },
    ];

    return customerprofile.contact.company ? arr : [arr[0]];
  };

  const [triggerMenu, setTriggerMenu] = useState(false);

  useEffect(() => {
    if (search.indexOf('profileTab') !== -1) {
      setTriggerMenu(true);
    }
  }, [search]);

  useEffect(() => {
    if (profileData && profileData.hasOwnProperty('user')) {
      client.writeQuery({ query: queryGetProfileData, data: { profileData: JSON.stringify(profileData) } });
    }
  }, [client, profileData]);

  useEffect(() => {
    if (localStorage.getItem('personalData')) {
      localStorage.removeItem('personalData');
    }
    if (localStorage.getItem('personalCustomerData')) {
      localStorage.removeItem('personalCustomerData');
    }
    if (localStorage.getItem('inventoryData')) {
      localStorage.removeItem('inventoryData');
    }
  }, []);

  const initialValues = {
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    telegram: '',
    birthDate: '',
    github: '',
    linkedin: '',
    avatar: '',
    title: '',
    address1: '',
    zip: '',
    emailBusiness: '',
    id: '',
    city: {},
    country: {},
  };

  const profileValidationSchema = useMemo(
    () =>
      Joi.object({
        email: Joi.string().allow('').email({ minDomainSegments: 2 }),
        firstName: Joi.string()
          .allow('')
          .regex(/^[a-zA-Z]*$/)
          .error(() => 'First Name must contain letters only'),
        lastName: Joi.string()
          .allow('')
          .regex(/^[a-zA-Z]*$/)
          .error(() => 'Last Name must contain letters only'),
        phone: Joi.string()
          .allow('')
          .max(13)
          .regex(/^\+[1-9][0-9]*[0-9]{2}[0-9]{3}[0-9]{2}[0-9]{2}$/)
          .error(() => 'Phone must be "+380 XX XXX XX XX"'),
        telegram: Joi.string().allow(''),
        avatar: Joi.string().allow(''),
        birthDate: Joi.string()
          .allow('')
          .regex(/([0-2][0-9]|3[0-1])\/(0[1-9]|1[0-2])\/([0-9]{4})$/)
          .error(() => 'Birth date must be "DD/MM/YYYY"'),
        github: Joi.string()
          .allow('')
          .regex(/^$|http(s)?:\/\/(www\.)?github\.com\/[A-z0-9_-]+\/?$/)
          .error(() => 'Invalid Github identifier'),
        linkedin: Joi.string()
          .allow('')
          .regex(/^$|http(s)?:\/\/([\w]+\.)?linkedin\.com\/in\/[A-z0-9_-]+\/?$/)
          .error(() => 'Invalid LinkedIn identifier'),
      }),
    []
  );

  const isEmailEditable = profileData?.user.permissions.includes(CAN_EDIT_COMPANY);

  const profileCustomerValidationSchema = useMemo(
    () =>
      Joi.object({
        email: Joi.string().email({ minDomainSegments: 2 }),
        firstName: Joi.string().allow(''),
        lastName: Joi.string().allow(''),
        emailBusiness: isEmailEditable ? Joi.string().email({ minDomainSegments: 2 }) : Joi.string().allow(''),
        title: Joi.string().allow(''),
        address1: Joi.string().allow(''),
        zip: Joi.string().allow(''),
      }),
    [isEmailEditable]
  );

  const submitFormRef = useRef(null);

  const handleTabClick = useCallback(
    (tabClickedId) => {
      if (tabClickedId !== parsedQuery.profileTab) {
        const newQuery = querystring.substitute(search, 'profileTab', tabClickedId);
        ReactGA.pageview(`${pathname}?${newQuery}`, 'Profile tab' + tabClickedId);

        history.replace(`${pathname}?${newQuery}`);
      }
    },
    [history, search, pathname, parsedQuery]
  );

  const handleLogOut = useCallback(async () => {
    ReactGA.event({
      category: 'Profile tab',
      action: 'Logout',
    });
    await logOut();
  }, [logOut]);

  const handleSaveChangesClick = useCallback(() => {
    submitFormRef.current && submitFormRef.current();
  }, []);

  const handleFormValidation = useCallback(
    (values) => {
      let validationResult = {};

      if (isPersonalTab && profileData && profileData.user.employeeprofile) {
        const _values = (({ email, firstName, lastName, phone, birthDate, github, linkedin, telegram, avatar }) => ({
          email,
          firstName,
          lastName,
          phone,
          birthDate,
          github,
          linkedin,
          telegram,
          avatar,
        }))(values);

        validationResult = Joi.validate(_values, profileValidationSchema, {
          abortEarly: false,
        });
      } else if ((isPersonalTab || isCompanyTab) && profileData && profileData.user.customerprofile) {
        const _values = (({ email, firstName, lastName, title, address1, zip, emailBusiness }) => ({
          email,
          firstName,
          lastName,
          title,
          address1,
          zip,
          emailBusiness,
        }))(values);

        validationResult = Joi.validate(_values, profileCustomerValidationSchema, {
          abortEarly: false,
        });
      }

      if (!validationResult.error) {
        setIsFormDisabled(false);
      } else {
        setIsFormDisabled(true);
        return validationResult.error;
      }
    },
    [isPersonalTab, profileData, isCompanyTab, profileValidationSchema, profileCustomerValidationSchema]
  );

  const onInventoryMutationSuccess = useCallback(() => {
    refetchProfileData();

    toast('Changes saved successfully', {
      type: toast.TYPE.SUCCESS,
      autoClose: 3000,
      closeButton: true,
      hideProgressBar: false,
    });
  }, [refetchProfileData]);

  const [profileInventoryData] = useMutation(mutationInventoryData, {
    onCompleted: onInventoryMutationSuccess,
    onError: onProfileMutationError,
  });

  const [profileCustomerData] = useMutation(mutationProfileCustomerPersonalData, {
    onCompleted: onProfileMutationSuccess,
    onError: onProfileMutationError,
  });

  const [isFormDisabled, setIsFormDisabled] = useState(true);
  const [inventories, setInventories] = useState([]);

  const handleFormSubmission = useCallback(
    (values, actions) => {
      const { setSubmitting } = actions;

      const {
        firstName,
        lastName,
        phone,
        birthDate,
        github,
        linkedin,
        telegram,
        avatar,
        title,
        address1,
        zip,
        email,
        emailBusiness,
        id,
        city,
        country,
      } = values;

      setSubmitting(true);
      setIsFormDisabled(true);

      if (isPersonalTab && profileData && profileData.user.employeeprofile) {
        ReactGA.event({
          category: 'Profile tab',
          action: 'Save profile',
        });
        showNotification();

        profileChangePersonalData({
          variables: {
            input: {
              user: {
                firstName,
                lastName,
              },
              phone,
              avatar:
                avatar && typeof avatar === 'string' && !avatar.includes('media') && !avatar.includes('avatar')
                  ? avatar.slice(avatar.indexOf('base64,') + 7)
                  : undefined,
              birthDate: moment(birthDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
              telegram,
              github,
              linkedin,
            },
          },
        });
      } else if (profileData && profileData.user.customerprofile) {
        showNotification();
        profileCustomerData({
          variables: {
            input: {
              firstName,
              lastName,
              email,
              companyEmail: emailBusiness,
              companyTitle: title,
              companyAddress1: address1,
              companyZip: zip,
              companyCity: city ? city.id : null,
              companyCountry: country ? country.id : null,
              clientMutationId: id,
            },
          },
        });
      } else if (isIinventoryTab) {
        ReactGA.event({
          category: 'Inventory tab',
          action: 'Save inventory',
        });
        const updateInventories = inventories.map((item) => {
          return {
            status: item.hasConfirmed ? 'confirmed' : 'declined',
            reference: item.reference,
          };
        });
        profileInventoryData({
          variables: {
            records: updateInventories,
          },
        });
      }
    },
    [
      isPersonalTab,
      profileData,
      isIinventoryTab,
      showNotification,
      profileChangePersonalData,
      profileCustomerData,
      inventories,
      profileInventoryData,
    ]
  );

  const renderTab = useCallback(
    (Tab, tabProps, data, onSetInventories) => <Tab {...tabProps} data={data} onSetInventories={onSetInventories} />,
    []
  );

  if (!parsedQuery.profileTab) {
    return null;
  }

  return (
    <CSSTransition
      in={triggerMenu}
      exit={!triggerMenu}
      timeout={300}
      mountOnEnter
      unmountOnExit
      onExited={() => {
        setTriggerMenu(false);
        history.replace(pathname);
        if (localStorage.getItem('personalData')) {
          localStorage.removeItem('personalData');
        }
        if (localStorage.getItem('personalCustomerData')) {
          localStorage.removeItem('personalCustomerData');
        }
        if (localStorage.getItem('inventoryData')) {
          localStorage.removeItem('inventoryData');
        }
      }}
    >
      <div className={cn('profile-page')} id="profile">
        <div className="profile-page__overlay" onClick={() => setTriggerMenu(false)} />
        <div className="profile">
          <div className="profile__top">
            <header className="profile__header">
              <h3 className="profile__header-text">Profile</h3>
              <Icon
                name="icon-close"
                className="profile__icon-close"
                onClick={() => setTriggerMenu(false)}
                dataTestId={'close-profile'}
              />
            </header>
            {profileData && (
              <>
                <div className="profile__navigation">
                  <Tabs
                    tabs={profileData.user.employeeprofile ? tabs : tabsCustomer(profileData.user.customerprofile)}
                    activeTabId={parsedQuery.profileTab}
                    onTabClick={handleTabClick}
                  />
                  <Button
                    text="Logout"
                    variant="secondary"
                    size={'small'}
                    className={'profile__btn-logout'}
                    dataTestId={'log-out'}
                    onClick={handleLogOut}
                  />
                </div>
                <Formik
                  initialValues={initialValues}
                  onSubmit={handleFormSubmission}
                  validate={handleFormValidation}
                  validateOnChange={true}
                  validateOnBlur={true}
                  render={(formikProps) => {
                    const { handleSubmit, submitForm } = formikProps;

                    submitFormRef.current = submitForm;

                    let TabComponent;

                    if (profileData.user.employeeprofile) {
                      switch (parsedQuery.profileTab) {
                        case 'personal':
                          TabComponent = TabPersonal;
                          break;
                        case 'inventory':
                          TabComponent = TabInventory;
                          break;
                        default:
                          TabComponent = null;
                      }
                    } else if (profileData.user.customerprofile) {
                      switch (parsedQuery.profileTab) {
                        case 'personal':
                          TabComponent = TabPersonalCustomer;
                          break;
                        case 'company':
                          TabComponent = TabCompany;
                          break;
                        default:
                          TabComponent = null;
                      }
                    }

                    if (profileDataGettingError) {
                      toast(profileDataGettingError.message, {
                        type: toast.TYPE.ERROR,
                        autoClose: 3000,
                        closeButton: true,
                        hideProgressBar: false,
                      });
                    }

                    return (
                      <>
                        {profileData && (
                          <form onSubmit={handleSubmit}>
                            {renderTab(
                              TabComponent,
                              {
                                formik: formikProps,
                                setIsFormDisabled,
                              },
                              profileData,
                              setInventories
                            )}
                          </form>
                        )}
                        {loadingProfileData && (
                          <Lottie
                            data-testid="tab_expenses_history"
                            className="lottie"
                            loop={true}
                            play={true}
                            animationData={animationData}
                            width={100}
                            style={{ width: 100, height: 100, margin: '0px auto' }}
                          />
                        )}
                      </>
                    );
                  }}
                ></Formik>
              </>
            )}
          </div>
          <div className="profile__footer">
            <Button
              className="profile__save-changes"
              dataTestId={'save-changes'}
              text="Save changes"
              onClick={handleSaveChangesClick}
              type={'submit'}
              disabled={isFormDisabled}
            />
          </div>
        </div>
      </div>
    </CSSTransition>
  );
};

export default withAuthentication(Profile);
